import React, { useContext, useEffect, useState } from "react";
import {
  Table,
  Space,
  Button,
  Input,
  Drawer,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import { PageHeader } from "../../../../components/page-header/page-header";
import useFetch from "../../../../hooks/useFetch";
import { AddEditProduct, GetProductList } from "../../../../utils/api.constant";
import { ePrivileges, eResultCode } from "../../../../utils/enum";
import useToast from "../../../../hooks/useToast";
import { ToastType, ToastOpen } from "../../../../state/toast/slice";
import CustomDrawer from "../../../../components/drawer";
import ProductForm from "./form";
import { TProduct } from "../../../../types/index";
import { UserContext } from "../../../../context";
import ActionCard from "../../../../components/actionCard/actionCard";
import { TContext, TFilterModel, TPrivilege } from "../../../../types/config";
import GenericPopover from "../../../../components/pop-over";
import {
  CheckCircleFilled,
  CheckOutlined,
  CloseCircleFilled,
  CloseOutlined,
  DeleteOutlined,
  EditFilled,
  FilterOutlined,
  IdcardFilled,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import utils from "../../../../utils";
import ColumnManager from "../../../../components/configurable-columns";
import ExportToCSV from "../../../../components/exporttocsv/ExportToCsv";
import DateUtils from "../../../../utils/date";
import { defaultFilterParams } from "../../../../utils/constants";
import {
  saveColumnPreferences,
  loadColumnPreferences,
  clearColumnPreferences,
} from "../../../../utils/column-management";
import CustomModal from "../../../../components/modal";
import Utils from "../../../../utils";
import { useColumnSearch } from "../../../../components/table/column-search";

const PRODUCT_LIST_COLUMN_KEY = "productList";

export default function ProductList() {
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TProduct[]>([]);
  const { getColumnSearchProps, getDateRangeFilterProps } =
    useColumnSearch<TProduct>();
  const [selectProduct, setSelectedProduct] = useState<TProduct | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const context: TContext = useContext(UserContext);

  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});

  const handleDelete = (product: TProduct) => {
    setSelectedProduct(product);
    setShowDeleteModal(true);
  };

  const initialColumns: any[] = [
    {
      title: "Airtech Model",
      dataIndex: "airtechModel",
      key: "airtechModel",
      // width: 180,
      filterSearch: true,
      sorter: (a: TProduct, b: TProduct) =>
        a.airtechModel?.localeCompare?.(b.airtechModel),
      onFilter: (value: string | number | boolean, record: TProduct) =>
        record.airtechModel?.toLowerCase() === (value as string).toLowerCase(),
    },
    {
      title: "Customer Name",
      dataIndex: "customerName",
      key: "customerName",
      // width: 180,
      filterSearch: true,
      sorter: (a: TProduct, b: TProduct) =>
        a.customerName?.localeCompare?.(b?.customerName),
      onFilter: (value: string | number | boolean, record: TProduct) =>
        record.customerName?.toLowerCase() === (value as string).toLowerCase(),
    },
    {
      title: "Customer Part No",
      dataIndex: "bePartNo",
      // width: 180,
      key: "bePartNo",
      filterSearch: true,
      sorter: (a: TProduct, b: TProduct) =>
        a.bePartNo?.localeCompare?.(b.bePartNo),
      onFilter: (value: string | number | boolean, record: TProduct) =>
        record.bePartNo?.toLowerCase() === (value as string).toLowerCase(),
    },
    {
      title: "Warranty (Months)",
      dataIndex: "warrantyDuration",
      // width: 180,
      key: "warrantyDuration",
      sorter: (a: TProduct, b: TProduct) =>
        a?.warrantyDuration - b?.warrantyDuration,
    },
    {
      title: "Price",
      dataIndex: "price",
      // width: 120,
      key: "price",
      sorter: (a: TProduct, b: TProduct) => a?.price - b?.price,
      render: (price: number) => `$ ${price}`,
    },
    {
      title: "Platform",
      dataIndex: "platform",
      key: "platform",
      // width: 160,
      filterSearch: true,
      sorter: (a: TProduct, b: TProduct) =>
        a.platform?.localeCompare?.(b?.platform),
      onFilter: (value: string | number | boolean, record: TProduct) =>
        record.platform?.toLowerCase() === (value as string).toLowerCase(),
    },
    {
      title: "Type of Unit",
      dataIndex: "typeOfUnit",
      key: "typeOfUnit",
      // width: 180,
      filterSearch: true,
      sorter: (a: TProduct, b: TProduct) =>
        a.typeOfUnit?.localeCompare?.(b.typeOfUnit),
      onFilter: (value: string | number | boolean, record: TProduct) =>
        record.typeOfUnit?.toLowerCase() === (value as string).toLowerCase(),
    },
    {
      title: "Acronym",
      // width: 350,
      dataIndex: "acronym",
      key: "acronym",
      sorter: (a: TProduct, b: TProduct) =>
        a.acronym?.localeCompare(b?.acronym),
      filterSearch: true,
      ...getColumnSearchProps("acronym"),
    },
    {
      title: "Description",
      // width: 350,
      dataIndex: "description",
      key: "description",
      sorter: (a: TProduct, b: TProduct) =>
        a.description?.localeCompare(b?.description),
      filterSearch: true,
      ...getColumnSearchProps("description"),
    },

    {
      title: "Action",
      key: "actions",
      align: "center",
      fixed: "right",
      // width: 80,
      render: (record: TProduct) => (
        <GenericPopover
          placement="left"
          content={
            <div className="standardActionBox">
              {Utils.isUserHasAccess(
                context.user?.privilegeList as TPrivilege[],
                ePrivileges.ADDEDITPRODUCT
              ) ? (
                <CustomDrawer
                  trigger={
                    <ActionCard text="Edit">
                      <EditFilled />
                    </ActionCard>
                  }
                  component={ProductForm}
                  componentProps={{
                    id: record.id,
                    onRefreshList: fetchData,
                  }}
                  title="Product Form"
                  size="default"
                />
              ) : null}

              {Utils.isUserHasAccess(
                context.user?.privilegeList as TPrivilege[],
                ePrivileges.DELETEPRODUCT
              ) ? (
                <ActionCard text="Delete" onClick={() => handleDelete(record)}>
                  <DeleteOutlined />
                </ActionCard>
              ) : null}
            </div>
          }
          icon={
            <MoreOutlined
              style={{
                fontSize: "20px",
                cursor: "pointer",
                display:
                  Utils.isUserHasAccess(
                    context.user?.privilegeList as TPrivilege[],
                    ePrivileges.ADDEDITPRODUCT
                  ) ||
                  Utils.isUserHasAccess(
                    context.user?.privilegeList as TPrivilege[],
                    ePrivileges.DELETEPRODUCT
                  )
                    ? "visible"
                    : "none",
              }}
            />
          }
        />
      ),
    },
  ];
  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);

  useEffect(() => {
    (async () => {
      const savedPreferences = await loadColumnPreferences(
        PRODUCT_LIST_COLUMN_KEY
      );
      if (savedPreferences) {
        const initialColumnsMap = new Map(
          initialColumns.map((col) => [col.key, col])
        );
        const reorderedColumns = savedPreferences
          .map((pref: any) => {
            const col = initialColumnsMap.get(pref.key);
            if (col) {
              return { ...col, hidden: pref.hidden };
            }
            return null;
          })
          .filter((col: any) => col !== null);

        setColumns(reorderedColumns as any[]);
        setDraftColumns(reorderedColumns as any[]);
      }
    })();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response = await post(GetProductList, requestPayload);
      const { dataResponse, data, filterModel } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map((item: TProduct, index: number) => ({
          ...item,
          key: (item.id ?? "").toString(),
          srNo: index + 1,
        }));
        setData(formattedData);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);
        updateColumnFilters(formattedData);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: "Failed to fetch product data",
      });
      setIsLoading(false);
    }
  };

  const updateColumnFilters = (dataset: TProduct[]) => {
    const getFilters = (key: keyof TProduct) => {
      const uniqueValues = Array.from(
        new Set(
          dataset
            ?.map((item) => item?.[key])
            ?.filter(
              (val) =>
                val !== null && val !== undefined && val !== "" && val !== "-"
            ) as string[]
        )
      );

      return uniqueValues.map((value) => ({ text: value, value }));
    };
    const airtechModelFilters = getFilters("airtechModel");
    const descriptionFilters = getFilters("description");
    const bePartNoFilters = getFilters("bePartNo");
    const customerNameFilters = getFilters("customerName");
    const typeOfUnitFilters = getFilters("typeOfUnit");
    const typeOfPlatform = getFilters("platform");

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "airtechModel") {
          return { ...col, filters: airtechModelFilters };
        }
        if (col.key === "description") {
          return { ...col, filters: descriptionFilters };
        }
        if (col.key === "bePartNo") {
          return { ...col, filters: bePartNoFilters };
        }
        if (col.key === "typeOfUnit") {
          return { ...col, filters: typeOfUnitFilters };
        }
        if (col.key === "customerName") {
          return { ...col, filters: customerNameFilters };
        }
        if (col.key === "platform") {
          return { ...col, filters: typeOfPlatform };
        }
        return col;
      })
    );
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    updateColumnFilters(filteredData);
  }, [activeFilters, data]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter((item) => {
    const searchMatch = Object.values(item).some(
      (val) =>
        val && val.toString().toLowerCase().includes(searchText.toLowerCase())
    );

    const airtechModelMatch =
      !activeFilters.airtechModel ||
      activeFilters.airtechModel.includes(item.airtechModel);

    const descriptionMatch = (() => {
      const f = activeFilters.description;
      if (!f || f.length === 0) return true;
      const text = String(item.description ?? "").toLowerCase();
      return f.some((val: any) =>
        String(val ?? "").toLowerCase() !== ""
          ? text.includes(String(val).toLowerCase())
          : false
      );
    })();

    const bePartNoMatch =
      !activeFilters.bePartNo || activeFilters.bePartNo.includes(item.bePartNo);

    return (
      searchMatch && airtechModelMatch && descriptionMatch && bePartNoMatch
    );
  });

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns);
    clearColumnPreferences(PRODUCT_LIST_COLUMN_KEY);
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns);
    saveColumnPreferences(PRODUCT_LIST_COLUMN_KEY, draftColumns);
    setDrawerVisible(false);
  };

  const visibleColumns = columns?.filter((col) => !col?.hidden);

  const exportHeaders = visibleColumns
    ?.filter(
      (column) =>
        column?.key && (column?.title as string)?.toLowerCase() !== "action"
    )
    .map((column) => ({
      label: column?.title,
      key: column?.key,
    }));

  const allKeys = Array.from(
    new Set(data.flatMap((item) => Object.keys(item)))
  );

  const exportData = data.map((user) => {
    const mapped = {
      ...user,
    };
    const completeRow: Record<string, any> = {};
    allKeys.forEach((key) => {
      const val = (mapped as any)[key];
      completeRow[key] =
        val === null ||
        val === undefined ||
        val === "" ||
        (Array.isArray(val) && val.length === 0)
          ? "-"
          : val;
    });

    return completeRow;
  });

  const handleResetAll = () => {
    setActiveFilters({});
    setSearchText("");
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
  };

  const deleteProduct = async (product: TProduct) => {
    try {
      const payload = {
        data: {
          ...product,
          isDeleted: 1,
        },
      };
      const response = await post(AddEditProduct, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
        fetchData();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleDeleteOk = async () => {
    if (selectProduct) {
      await deleteProduct(selectProduct);
    }
    setSelectedProduct(null);
    setShowDeleteModal(false);
  };

  const handleDeleteCancel = () => {
    setSelectedProduct(null);
    setShowDeleteModal(false);
  };

  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardFilled />}
        title={`${filterParams.totalRows} Total Products`}
        size="large"
      >
        <Space className="headerItems" wrap>
          {/* {Utils.isUserHasAccess(
            context.user?.privilegeList as TPrivilege[],
            ePrivileges.ADDCUSTOMERS
          ) ? ( */}
          <CustomDrawer
            trigger={
              <Button disabled={isLoading} type="primary">
                Add
              </Button>
            }
            component={ProductForm}
            componentProps={{
              id: 0,
              onRefreshList: fetchData,
            }}
            title="Product Form"
            size="default"
          />
          {/* ) : null} */}
          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={exportData}
            headers={exportHeaders}
            fileName={`product_${DateUtils.format(new Date())}.csv`}
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined
              onClick={() => {
                setDraftColumns(columns);
                setDrawerVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          // sticky
          scroll={{
            x: "max-content",
          }}
        />
      </div>

      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
      <CustomModal
        title="Confirm Delete"
        isOpen={showDeleteModal}
        onOk={handleDeleteOk}
        onCancel={() => handleDeleteCancel()}
        okText="Yes"
        cancelText="No"
        cusHeader={true}
      >
        <p>
          Are you about to delete <b>{selectProduct?.airtechModel}</b>?
        </p>
      </CustomModal>
    </div>
  );
}
